/**
 * Calendar helpers for the reporting zone. Reports, cohorts and schedules all read the
 * calendar in Europe/Prague (spec DATE-01): a day is [00:00, next 00:00) Prague time,
 * whatever the UTC offset that day happens to have.
 */
export const REPORTING_TIME_ZONE = 'Europe/Prague';

export interface WallClock {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

/** Calendar fields of an instant as seen in `timeZone`. */
export function wallClock(date: Date, timeZone: string = REPORTING_TIME_ZONE): WallClock {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(date);
  const get = (type: Intl.DateTimeFormatPartTypes): number => Number(parts.find((p) => p.type === type)?.value ?? '0');
  return {
    year: get('year'),
    month: get('month'),
    day: get('day'),
    hour: get('hour'),
    minute: get('minute'),
    second: get('second'),
  };
}

/** UTC offset of `timeZone` at `date`, in ms (positive east of Greenwich). */
function offsetMs(date: Date, timeZone: string): number {
  const w = wallClock(date, timeZone);
  const asUtc = Date.UTC(w.year, w.month - 1, w.day, w.hour, w.minute, w.second);
  return asUtc - Math.floor(date.getTime() / 1000) * 1000;
}

/**
 * The instant at which `timeZone` shows the given wall-clock time. Two passes converge on
 * the right offset across a DST switch; a time that does not exist (spring-forward gap)
 * resolves to the same instant as one hour later, an ambiguous one (autumn) to its first
 * occurrence. `day` may overflow the month (Date.UTC normalises it).
 */
export function zonedTimeToUtc(
  wall: { year: number; month: number; day: number; hour?: number; minute?: number },
  timeZone: string = REPORTING_TIME_ZONE,
): Date {
  const guess = Date.UTC(wall.year, wall.month - 1, wall.day, wall.hour ?? 0, wall.minute ?? 0);
  const first = guess - offsetMs(new Date(guess), timeZone);
  const second = guess - offsetMs(new Date(first), timeZone);
  return new Date(second);
}

const ISO_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Start of the calendar day `YYYY-MM-DD` in `timeZone`; throws on a malformed date. */
export function dayStartUtc(isoDate: string, timeZone: string = REPORTING_TIME_ZONE): Date {
  const m = ISO_DATE_RE.exec(isoDate);
  if (!m) throw new Error(`not an ISO date: ${isoDate}`);
  return zonedTimeToUtc({ year: Number(m[1]), month: Number(m[2]), day: Number(m[3]) }, timeZone);
}

/** `YYYY-MM-DD` shifted by `days` calendar days (pure date arithmetic, no time zone involved). */
export function addDays(isoDate: string, days: number): string {
  const m = ISO_DATE_RE.exec(isoDate);
  if (!m) throw new Error(`not an ISO date: ${isoDate}`);
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + days));
  return d.toISOString().slice(0, 10);
}

/** The calendar date (`YYYY-MM-DD`) of an instant in `timeZone`. */
export function isoDateInZone(date: Date, timeZone: string = REPORTING_TIME_ZONE): string {
  const w = wallClock(date, timeZone);
  return `${w.year}-${String(w.month).padStart(2, '0')}-${String(w.day).padStart(2, '0')}`;
}

/** Inclusive calendar-day count between two ISO dates (same day = 1). */
export function calendarDays(fromIsoDate: string, toIsoDate: string): number {
  const a = dayStartUtc(fromIsoDate, 'UTC').getTime();
  const b = dayStartUtc(toIsoDate, 'UTC').getTime();
  return Math.round((b - a) / 86_400_000) + 1;
}
